<script setup lang="ts">
import type { SidebarItem } from '~/data/studentNavs'

defineProps({
  links: { type: Array as PropType<SidebarItem[]>, required: true },
  role: { type: String, required: true }
})

const emit = defineEmits(['closeMenu', 'handleLogout'])

const { getStudentSidebarLinks } = useStaticData()
const showSublinks = ref('')

const navbarNavigateTo = (link: string) => {
  emit('closeMenu')
  navigateTo(link)
}

const clickMenuLink = (link: SidebarItem) => {
  if (!link.dropdown) navbarNavigateTo(link.link || '')
  else if (showSublinks.value === '') showSublinks.value = link.title
  else if (showSublinks.value === link.title) showSublinks.value = ''
  else showSublinks.value = link.title
}
</script>

<template>
  <div v-for="link in links" :key="`student-link-${link.title}`" class="link">
    <div
      :class="`link-header clickable ${showSublinks === link.title ? 'link-header-active' : ''}`"
      @click="clickMenuLink(link)"
    >
      <component :is="link.icon" class="icon" />

      <span>
        {{ link.title }}
      </span>

      <CommonIconChevron v-if="link.dropdown" class="chevron" />
    </div>

    <div :class="`link-sublink ${showSublinks === link.title ? 'show' : ''}`">
      <div
        v-for="sublink in getStudentSidebarLinks(link.title.toLowerCase(), role)"
        :key="`sublink-${sublink.title}-link-${link.title}`"
        class="sublink clickable"
        @click="navbarNavigateTo(sublink.link || '/')"
      >
        <component :is="sublink.icon" class="icon icon-sub" />
        <span>{{ sublink.title }}</span>
        <CommonIconChevron class="chevron chevron-sub" />
      </div>
    </div>
  </div>

  <div class="link">
    <div class="link-header clickable" @click="navbarNavigateTo('/profile')">
      <CommonIconSidebarProfile class="icon" />
      <span>Profile</span>
    </div>
  </div>

  <div class="link">
    <div class="link-header clickable" @click="navbarNavigateTo('/settings')">
      <CommonIconSidebarSettings class="icon" />
      <span>Settings</span>
    </div>
  </div>

  <div class="link">
    <div class="link-header clickable" @click="$emit('handleLogout')">
      <CommonIconNavbarLogout class="icon" />
      <span>Log out</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.link {
  border-bottom: 1px solid var(--borderL2);
  padding: 0 30px;

  &-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    padding: 30px 0;
    gap: 15px;
    align-items: center;
  }

  .icon {
    $iconSize: 17px;
    height: $iconSize;
    width: $iconSize;
    fill: var(--textM2);
  }

  .chevron {
    $chevronSize: 14px;
    height: $chevronSize;
    width: $chevronSize;
    transform: rotate(90deg);
    transition: transform 0.3s ease-out;
  }

  &-header-active {
    .chevron {
      transform: rotate(90deg) scaleX(-1);
    }
  }
}

.link-sublink {
  margin-top: -10px;
  transition:
    height 0.3s ease-out,
    padding 0.3s ease-out;
  overflow: hidden;
  height: 0;
  padding: 0 30px;
}

.show {
  padding: 0 30px 20px;
  height: auto;
}

.sublink {
  padding: 15px 0;
  display: flex;
  align-items: center;
  gap: 13px;

  .icon-sub {
    fill: var(--purple);
    height: 18px;
    width: 18px;
  }

  .chevron-sub {
    transform: none;
    height: 10px;
    width: 10px;
  }
}
</style>
